import { useState } from 'react';
import { Button } from '~/shared/shadcn/ui/button';
import { Badge } from '~/shared/shadcn/ui/badge';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from '~/shared/shadcn/ui/sheet';
import { AlertTriangle, Download, CheckCircle2, X } from 'lucide-react';
import { ScrollArea } from '~/shared/shadcn/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '~/shared/shadcn/ui/tabs';
import { Avatar, AvatarFallback } from '~/shared/shadcn/ui/avatar';
import type { PerformanceConflict } from './conflict-details-modal';
import { useCurrentStudio } from '~entities/studios/model/studio-store';
import { formatRecitalDate } from '~shared/utils/recital-timezone';

interface ConflictResolutionDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  conflicts: PerformanceConflict[];
  onResolveConflict?: (conflictId: string) => Promise<void>;
}

export function ConflictResolutionDrawer({
  isOpen,
  onClose,
  conflicts,
  onResolveConflict,
}: ConflictResolutionDrawerProps) {
  const [activeTab, setActiveTab] = useState<'all' | 'critical' | 'warnings'>('all');
  const [resolvingConflicts, setResolvingConflicts] = useState<Set<string>>(new Set());

  // Get studio timezone
  const studio = useCurrentStudio();
  const timezone = studio?.timezone || 'UTC';

  const errorConflicts = conflicts.filter((c) => c.severity === 'error');
  const warningConflicts = conflicts.filter((c) => c.severity === 'warning');

  const displayedConflicts =
    activeTab === 'all' ? conflicts : activeTab === 'critical' ? errorConflicts : warningConflicts;

  const handleExportCSV = () => {
    // Create CSV content
    const headers = ['Type', 'Severity', 'Description', 'Performance', 'Students', 'Status'];
    const rows = conflicts.map((c) => [
      c.conflictType,
      c.severity,
      c.description,
      getPerformanceName(c.performanceId as any),
      getStudentNames(c),
      c.resolved ? 'Resolved' : 'Unresolved',
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(',')),
    ].join('\n');

    // Download CSV
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `conflicts-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const handleResolve = async (conflictId: string) => {
    if (!onResolveConflict) return;

    setResolvingConflicts((prev) => new Set(prev).add(conflictId));
    try {
      await onResolveConflict(conflictId);
    } catch (error) {
      console.error('Error resolving conflict:', error);
    } finally {
      setResolvingConflicts((prev) => {
        const next = new Set(prev);
        next.delete(conflictId);
        return next;
      });
    }
  };

  const getInitials = (name: string) => {
    const parts = name.split(' ');
    return parts.length >= 2 && parts[0] && parts[1]
      ? `${parts[0][0]}${parts[1][0]}`
      : parts[0]?.[0] || '';
  };

  const getConflictTypeName = (type: string) => {
    switch (type) {
      case 'insufficient_gap':
        return 'Insufficient Gap';
      case 'student_overload':
        return 'Student Overload';
      case 'missing_required_data':
        return 'Missing Required Data';
      case 'time_overlap':
        return 'Time Overlap';
      case 'age_data_issue':
        return 'Age Data Issue';
      case 'class_conflict':
        return 'Class Conflict';
      case 'insufficient_break':
        return 'Insufficient Break';
      default:
        return type.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    }
  };

  const getConflictTypeColor = (type: string) => {
    switch (type) {
      case 'insufficient_gap':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'student_overload':
        return 'bg-red-100 text-red-700 border-red-200';
      case 'missing_required_data':
        return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      case 'time_overlap':
        return 'bg-purple-100 text-purple-700 border-purple-200';
      case 'age_data_issue':
        return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'class_conflict':
        return 'bg-indigo-100 text-indigo-700 border-indigo-200';
      case 'insufficient_break':
        return 'bg-pink-100 text-pink-700 border-pink-200';
      default:
        return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  // Helper to get performance name
  const getPerformanceName = (performanceId: string | { _id: string; name: string }): string => {
    if (typeof performanceId === 'string') {
      return performanceId.slice(-8); // Fallback to ID if not populated
    }
    return performanceId.name || performanceId._id.slice(-8);
  };

  // Helper to get student names
  const getStudentNames = (conflict: PerformanceConflict): string => {
    const studentIds = (conflict.relatedStudentIds || []).filter(
      (student): student is string | { _id: string; firstName: string; lastName: string } =>
        student != null,
    );

    const fromIds = studentIds
      .map((student) => {
        if (typeof student === 'string') return null;
        const name = `${student.firstName ?? ''} ${student.lastName ?? ''}`.trim();
        return name || null;
      })
      .filter((name): name is string => Boolean(name));

    if (fromIds.length > 0) return fromIds.join(', ');
    if (conflict.metadata?.studentNames?.length) {
      return conflict.metadata.studentNames.join(', ');
    }
    if (conflict.metadata?.studentName) return conflict.metadata.studentName;
    return 'No students';
  };

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="right" className="w-full sm:max-w-xl flex flex-col p-0">
        <SheetHeader className="px-6 pt-6 pb-4 border-b">
          <SheetTitle className="flex items-center gap-2">
            <AlertTriangle className="h-5 w-5 text-orange-600" />
            Resolve Conflicts
          </SheetTitle>
          <SheetDescription>Review and resolve scheduling conflicts for this recital</SheetDescription>
        </SheetHeader>

        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as typeof activeTab)}
          className="flex-1 flex flex-col min-h-0"
        >
          <div className="px-6 pt-4">
            <TabsList className="grid w-full grid-cols-3">
              <TabsTrigger value="all" className="gap-2">
                All
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs">
                  {conflicts.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="critical" className="gap-2">
                Critical
                <Badge variant="secondary" className="ml-1 h-5 px-1.5 text-xs bg-red-100 text-red-700">
                  {errorConflicts.length}
                </Badge>
              </TabsTrigger>
              <TabsTrigger value="warnings" className="gap-2">
                Warnings
                <Badge
                  variant="secondary"
                  className="ml-1 h-5 px-1.5 text-xs bg-orange-100 text-orange-700"
                >
                  {warningConflicts.length}
                </Badge>
              </TabsTrigger>
            </TabsList>
          </div>

          <TabsContent value={activeTab} className="flex-1 mt-0 overflow-hidden min-h-0">
            <ScrollArea className="h-full px-6 py-4">
              <div className="space-y-4 pb-4">
                {displayedConflicts.length > 0 ? (
                  displayedConflicts.map((conflict) => {
                    const isResolving = resolvingConflicts.has(conflict._id);
                    const isResolved = conflict.resolved;

                    return (
                      <div
                        key={conflict._id}
                        className={`rounded-lg border p-4 transition-all duration-200 ${
                          conflict.severity === 'error'
                            ? 'border-red-200 bg-red-50'
                            : 'border-orange-200 bg-orange-50'
                        } ${isResolved ? 'opacity-60' : ''}`}
                      >
                        <div className="flex items-start gap-3 mb-3">
                          <Avatar className="h-10 w-10 border-2 border-background">
                            <AvatarFallback className="bg-primary text-primary-foreground text-sm font-semibold">
                              {getInitials(conflict.conflictType)}
                            </AvatarFallback>
                          </Avatar>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 flex-wrap mb-2">
                              <h4 className="font-semibold text-sm">
                                {getConflictTypeName(conflict.conflictType)}
                              </h4>
                              <Badge
                                variant="outline"
                                className={`text-xs ${getConflictTypeColor(conflict.conflictType)}`}
                              >
                                {conflict.conflictType}
                              </Badge>
                              {isResolved && (
                                <Badge variant="outline" className="text-xs bg-green-100 text-green-700 border-green-200">
                                  Resolved
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-foreground/80">{conflict.description}</p>
                          </div>
                        </div>

                        {conflict.relatedPerformanceIds.length > 0 && (
                          <div className="space-y-2 text-xs mb-3">
                            <div className="font-medium text-muted-foreground">
                              Related Performances:
                            </div>
                            {conflict.relatedPerformanceIds.map((perfId, idx) => (
                              <div
                                key={idx}
                                className="flex items-center gap-2 pl-4 py-1.5 rounded bg-white/50"
                              >
                                <span className="font-medium">
                                  {getPerformanceName(perfId as any)}
                                </span>
                              </div>
                            ))}
                          </div>
                        )}

                        {(conflict.relatedStudentIds?.length > 0 ||
                          conflict.metadata?.studentName ||
                          conflict.metadata?.studentNames?.length) && (
                          <div className="flex flex-col gap-1 py-2 px-3 rounded bg-white/50 text-xs mb-3">
                            <span className="font-medium text-muted-foreground">Students affected:</span>
                            <span className="font-semibold">
                              {getStudentNames(conflict)}
                            </span>
                          </div>
                        )}

                        {!isResolved && onResolveConflict && (
                          <div className="flex gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              className="flex-1 gap-2 h-8 text-xs"
                              onClick={() => handleResolve(conflict._id)}
                              disabled={isResolving}
                            >
                              {isResolving ? (
                                'Resolving...'
                              ) : (
                                <>
                                  <CheckCircle2 className="h-3 w-3" />
                                  Mark as Resolved
                                </>
                              )}
                            </Button>
                          </div>
                        )}

                        {isResolved && (
                          <div className="flex items-center gap-2 text-xs text-green-700">
                            <CheckCircle2 className="h-4 w-4" />
                            <span className="font-medium">
                              Resolved {conflict.resolvedAt && `on ${formatRecitalDate(conflict.resolvedAt, timezone, 'MM/dd/yyyy')}`}
                            </span>
                          </div>
                        )}
                      </div>
                    );
                  })
                ) : (
                  <div className="flex flex-col items-center justify-center py-12 text-center">
                    <div className="mb-3 rounded-full bg-green-100 p-3">
                      <CheckCircle2 className="h-6 w-6 text-green-600" />
                    </div>
                    <h3 className="text-lg font-semibold">
                      No {activeTab === 'all' ? '' : activeTab} Conflicts
                    </h3>
                    <p className="mt-1 text-sm text-muted-foreground">
                      {activeTab === 'all'
                        ? 'All performances are properly scheduled'
                        : `No ${activeTab} conflicts found`}
                    </p>
                  </div>
                )}
              </div>
            </ScrollArea>
          </TabsContent>
        </Tabs>

        {conflicts.length > 0 && (
          <SheetFooter className="px-6 py-4 border-t bg-muted/30">
            <div className="flex gap-2 w-full">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportCSV}
                className="gap-2 flex-1"
              >
                <Download className="h-4 w-4" />
                Export CSV
              </Button>
              <Button variant="outline" size="sm" onClick={onClose} className="gap-2 flex-1">
                <X className="h-4 w-4" />
                Close
              </Button>
            </div>
          </SheetFooter>
        )}
      </SheetContent>
    </Sheet>
  );
}
